// Phase 45.5 — <HinDuplicateNotice /> (INGEST-01, D-18).
//
// A SLIM, one-line inline notice (NOT an alarming block / NOT a modal) shown
// when the HIN extracted/entered for the boat already exists in the tenant's
// inventory. Stops the dealer at the right moment without nagging.
//
// Copy is verbatim per UI-SPEC §"HIN-duplicate notice":
//   "This HIN is already in your inventory" + quiet links "View existing · Use anyway"

import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HinDuplicateNoticeProps {
  /** Only renders when true (the Task-1 hin-duplicate endpoint returned a hit). */
  show: boolean;
  /** Existing boat id — "View existing" navigates here. */
  existingBoatId: string | null;
  /** "View existing" — open the existing boat (parent decides navigation). */
  onViewExisting?: (boatId: string) => void;
  /** "Use anyway" — dismiss the notice and continue creating the duplicate. */
  onUseAnyway?: () => void;
}

export function HinDuplicateNotice({
  show,
  existingBoatId,
  onViewExisting,
  onUseAnyway,
}: HinDuplicateNoticeProps) {
  if (!show) return null;

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-sm px-2 py-1',
        // Slim amber inline notice — informative, not alarming (NOT destructive red).
        'bg-amber-50 dark:bg-amber-950/30',
        'text-[0.8125rem] font-normal text-amber-800 dark:text-amber-200',
      )}
      role="status"
    >
      <Info className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
      <span>This HIN is already in your inventory</span>
      <span className="text-muted-foreground" aria-hidden="true">
        ·
      </span>
      <button
        type="button"
        onClick={() => existingBoatId && onViewExisting?.(existingBoatId)}
        disabled={!existingBoatId}
        className="text-violet-700 hover:underline disabled:opacity-50 dark:text-violet-300"
      >
        View existing
      </button>
      <span className="text-muted-foreground" aria-hidden="true">
        ·
      </span>
      <button
        type="button"
        onClick={() => onUseAnyway?.()}
        className="text-violet-700 hover:underline dark:text-violet-300"
      >
        Use anyway
      </button>
    </div>
  );
}
